import { useState } from 'react'
import { Check, Monitor, MonitorSmartphone, Smartphone, Tablet } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  applyViewMode,
  getViewMode,
  VIEW_MODE_META,
  VIEW_MODES,
  type ViewMode,
} from '@/lib/viewMode'

/**
 * The desktop view switcher: one icon in the nav bar showing the mode currently
 * in force, with the choice behind it.
 *
 * A three-icon segmented control put a permanent, always-lit widget in the top
 * bar for something touched roughly never, and carried as much visual weight as
 * the notification bell and the account chip combined. This is the same pattern
 * as `AccountMenu`, which is the neighbour it sits beside.
 *
 * **Desktop only, and this file must stay that way.** It imports the Radix
 * popover. When the mobile controls lived in this module too, Rollup put the
 * whole thing in the chunk both html entries share and the mobile bundle paid
 * 16 KB gzipped for a control it never renders. The mobile equivalents are in
 * `src/mobile/components/ViewModeControls.tsx`; only the wording is shared, via
 * `VIEW_MODE_META`.
 */
const ICONS: Record<ViewMode, typeof Monitor> = {
  auto: MonitorSmartphone,
  desktop: Monitor,
  tablet: Tablet,
  mobile: Smartphone,
}

export function ViewModeSwitcher({ className }: { className?: string }) {
  // Read once on mount. The value only changes through this control, and every
  // change that matters navigates away, so there is nothing to subscribe to.
  const [mode, setMode] = useState<ViewMode>(getViewMode)
  const [open, setOpen] = useState(false)

  const CurrentIcon = ICONS[mode]

  function choose(value: ViewMode) {
    setOpen(false)
    setMode(value)
    applyViewMode(value)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type='button'
          aria-label={`View: ${VIEW_MODE_META[mode].label}. Change how this site is displayed.`}
          title={`View: ${VIEW_MODE_META[mode].label}`}
          className={cn(
            'flex h-8 w-8 shrink-0 items-center justify-center rounded-md transition-colors',
            // Tinted only while a surface is forced, so Auto is quiet and an
            // override is visible at a glance without reading the icon.
            mode === 'auto'
              ? 'text-muted-foreground hover:bg-muted hover:text-foreground'
              : 'bg-primary/10 text-primary hover:bg-primary/15',
            className,
          )}
        >
          <CurrentIcon className='h-4 w-4' aria-hidden='true' />
        </button>
      </PopoverTrigger>

      <PopoverContent align='end' className='w-44 p-1'>
        <ul>
          {VIEW_MODES.map((value) => {
            const { label, hint } = VIEW_MODE_META[value]
            const Icon = ICONS[value]
            const active = value === mode
            return (
              <li key={value}>
                <button
                  type='button'
                  onClick={() => choose(value)}
                  aria-current={active}
                  // The hint is the tooltip rather than a second line: three
                  // one-word choices do not need explaining in place, and the
                  // description made a rarely-used menu three times taller.
                  title={hint}
                  className='flex w-full items-center gap-2.5 rounded px-2 py-1.5 text-left transition-colors hover:bg-muted'
                >
                  <Icon
                    className={cn(
                      'h-4 w-4 shrink-0',
                      active ? 'text-primary' : 'text-muted-foreground',
                    )}
                    aria-hidden='true'
                  />
                  <span className={cn('flex-1 text-sm text-foreground', active && 'font-medium')}>
                    {label}
                  </span>
                  {active && <Check className='h-4 w-4 shrink-0 text-primary' aria-hidden='true' />}
                </button>
              </li>
            )
          })}
        </ul>
      </PopoverContent>
    </Popover>
  )
}
